import React from 'react';

export const DealLifecycleSection = () => {
  return (
    <section id="lifecycle" className="scroll-mt-16 py-24 bg-[#060A13] border-b border-slate-800 text-white relative overflow-hidden">
      {/* Ambient Background Glows */}
      <div className="absolute -top-24 right-0 w-[550px] h-[550px] bg-cyan-500/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute top-1/2 -left-20 w-[550px] h-[550px] bg-[#F58220]/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 relative z-10">

        {/* Header Lockup */}
        <div className="max-w-3xl mb-16">
          <span className="text-xs font-mono uppercase tracking-widest text-[#F58220] font-semibold block mb-3">
            AUTONOMOUS WORKFLOW ORCHESTRATION
          </span>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight mb-4 leading-tight">
            The Full Deal Lifecycle.<br className="hidden sm:inline" /> Accelerated.
          </h2>
          <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
            Frontier Deal OS&#8482; deploys across each stage of the deal lifecycle to accelerate complex workflows—empowering teams to stress-test more hypotheses with deep sector-specific context and underwrite with absolute conviction.
          </p>
        </div>

        {/* 4-Column Clean Editorial Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">

          {/* CARD 01: SOURCING & SCREENING */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200 flex flex-col transition-all duration-300 group hover:-translate-y-1 hover:border-[#F58220]/60 hover:shadow-[0_14px_34px_-14px_rgba(15,23,42,0.22)]">
            <div className="min-h-[180px]">
              <div className="mb-5 font-mono text-xs text-slate-500 transition-colors duration-300">
                <span className="font-bold group-hover:text-[#F58220] transition-colors">01</span>
                <span className="mx-2">/</span>
                <span className="uppercase tracking-wider group-hover:text-[#F58220]/80 transition-colors">Sourcing</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 tracking-tight">Strategic Sector Mapping</h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mb-4 font-sans group-hover:text-slate-700 transition-colors">
                Synthesizes macro industry profit pools and builds custom alpha theses to identify, rank, and screen off-market targets before an auction process forms.
              </p>
            </div>
            <div className="pt-4 border-t border-slate-200 group-hover:border-[#F58220]/30 transition-colors duration-300">
              <ul className="space-y-2.5 text-xs font-sans text-slate-600">
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Thematic Sector &amp; Profit-Pool Mapping</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Automated CIM Triaging &amp; Screening</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Alpha Thesis &amp; Red-Flag Formulation</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Acquirer &amp; Competitor Universe Mapping</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CARD 02: DUE DILIGENCE & CDD */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200 flex flex-col transition-all duration-300 group hover:-translate-y-1 hover:border-[#F58220]/60 hover:shadow-[0_14px_34px_-14px_rgba(15,23,42,0.22)]">
            <div className="min-h-[180px]">
              <div className="mb-5 font-mono text-xs text-slate-500 transition-colors duration-300">
                <span className="font-bold group-hover:text-[#F58220] transition-colors">02</span>
                <span className="mx-2">/</span>
                <span className="uppercase tracking-wider group-hover:text-[#F58220]/80 transition-colors">Diligence</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 tracking-tight">Strategic &amp; Investment Diligence</h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mb-4 font-sans group-hover:text-slate-700 transition-colors">
                Reconciles raw dataroom evidence with outside-in market intelligence to stress-test commercial moats, technical defensibility, and downside risk.
              </p>
            </div>
            <div className="pt-4 border-t border-slate-200 group-hover:border-[#F58220]/30 transition-colors duration-300">
              <ul className="space-y-2.5 text-xs font-sans text-slate-600">
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>VDR Ingestion &amp; DD Checklist Audit</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Market Dynamics &amp; Addressable TAM</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Deterministic Valuation &amp; Synergies</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Adversarial Diligence &amp; IC Verdict</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CARD 03: VALUE CREATION */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200 flex flex-col transition-all duration-300 group hover:-translate-y-1 hover:border-[#F58220]/60 hover:shadow-[0_14px_34px_-14px_rgba(15,23,42,0.22)]">
            <div className="min-h-[180px]">
              <div className="mb-5 font-mono text-xs text-slate-500 transition-colors duration-300">
                <span className="font-bold group-hover:text-[#F58220] transition-colors">03</span>
                <span className="mx-2">/</span>
                <span className="uppercase tracking-wider group-hover:text-[#F58220]/80 transition-colors">Value Creation</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 tracking-tight">Topline Alpha &amp; Expansion</h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mb-4 font-sans group-hover:text-slate-700 transition-colors">
                Deploys agentic capabilities inside portfolio assets to unlock new revenue streams, launch adjacent markets, and transform legacy business models.
              </p>
            </div>
            <div className="pt-4 border-t border-slate-200 group-hover:border-[#F58220]/30 transition-colors duration-300">
              <ul className="space-y-2.5 text-xs font-sans text-slate-600">
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Continuous PortCo Telemetry Monitoring</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Programmatic Bolt-On M&amp;A Pipelines</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Topline &amp; Adjacent Market Expansion</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Boardroom &amp; Operating Committee Packs</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CARD 04: STRATEGIC EXIT */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200 flex flex-col transition-all duration-300 group hover:-translate-y-1 hover:border-[#F58220]/60 hover:shadow-[0_14px_34px_-14px_rgba(15,23,42,0.22)]">
            <div className="min-h-[180px]">
              <div className="mb-5 font-mono text-xs text-slate-500 transition-colors duration-300">
                <span className="font-bold group-hover:text-[#F58220] transition-colors">04</span>
                <span className="mx-2">/</span>
                <span className="uppercase tracking-wider group-hover:text-[#F58220]/80 transition-colors">Strategic Exit</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 tracking-tight">Multiple Realization</h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mb-4 font-sans group-hover:text-slate-700 transition-colors">
                Reverse-engineers strategic acquirer capability gaps 12 months early, packaging mathematically verified equity stories that command premium multiples.
              </p>
            </div>
            <div className="pt-4 border-t border-slate-200 group-hover:border-[#F58220]/30 transition-colors duration-300">
              <ul className="space-y-2.5 text-xs font-sans text-slate-600">
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Strategic Acquirer Profiling &amp; Gap Mapping</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Vendor Commercial Diligence (VDD) Defense</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Clean-Room &amp; Certified Evidence Repositories</span>
                </li>
                <li className="flex items-start gap-2.5 group-hover:text-slate-900 transition-colors">
                  <span className="text-slate-400 group-hover:text-[#F58220] transition-colors select-none">—</span>
                  <span>Exit Equity Story &amp; Multiple Positioning</span>
                </li>
              </ul>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
